import { Alert, Box, Button, ExpandableSection, SpaceBetween, Tabs } from '@amzn/awsui-components-react';
import TPTaxReportTableGrid from 'src/components/reports/TPTaxReportTableGrid';
import React, { useCallback, useMemo } from 'react';
import { FetchReportRequest } from 'src/models/reports/FetchReport';
import { ReportMetaData, TPTaxReportData, TPTaxReportMapResponse } from 'src/models/reports/TPTaxReport';
import ServiceCollection from 'src/services/ServiceCollection';
import CONSTANTS, {
    isPliTabKey,
    IS_TESTED_PARTY_REPORT_ENABLED,
    LEF_DRILLDOWN_DIMENSION_ACCOUNT_ID,
} from 'src/utils/constants';
import { useHistory } from 'react-router-dom';
import { MANAGE_EXCEPTIONS_TAB_URL_PREFIX } from 'src/components/ic-account-mapping/constants';
import {
    MultiCLIPassthroughCostSection,
    MultiCLIPLIReconciliationSection,
} from 'src/components/reports/PassthroughCostSections';
import { buildCliHeaderLabels, formatCliWithCurrency, buildCliCurrencyMap } from 'src/components/reports/TPMultiCLIReportsShared';
import DrillDownDimensionSelector from 'src/components/reports/lefReport/DrillDownDimensionSelector';
import {
    dimensionUnavailableMessage,
    filterZeroSegmentRows,
    getDrillDownDimension,
    isAccountDimension,
    isSegmentRow,
    resolveDimensionView,
    SEGMENT_DESCRIPTION_UNAVAILABLE,
    splitSegmentDisplayName,
} from 'src/components/reports/segmentDrillDown';
import {
    buildProvenanceColumns,
    hasDrillableSegments,
    mergeCliValuesIntoRows,
    ProvenanceColumn,
    segmentCategoryFromHeader,
    stickyColumnCount,
} from 'src/components/reports/cliProvenance';
import {
    SegmentRequestContext,
    useSegmentCliExpansion,
} from 'src/components/reports/lefReport/useSegmentCliExpansion';
import { CliColumnHeader, SegmentTotalHeader } from 'src/components/reports/lefReport/CliProvenanceHeaders';
import { openCliInCalcBuilder } from 'src/components/reports/lefReport/calcBuilderLink';
import { useCliSources } from 'src/components/reports/lefReport/useCliSources';
import { CliSourcesFetchers } from 'src/components/reports/lefReport/cliSources';
import { LefCliProvenanceContext } from 'src/components/reports/lefReport/LefCliProvenanceContext';

/** Filters a CLI-keyed map to only include CLIs present in the given visible-CLI list. */
function filterCliMap<T>(cliMap: { [cli: string]: T } | null | undefined, visibleHeaders: string[] | undefined): { [cli: string]: T } | null | undefined {
    if (!cliMap || !visibleHeaders || visibleHeaders.length === 0) return cliMap;
    const visibleCLIs = new Set(visibleHeaders);
    const filtered: { [cli: string]: T } = {};
    for (const cli of Object.keys(cliMap)) {
        if (visibleCLIs.has(cli)) {
            filtered[cli] = cliMap[cli];
        }
    }
    return Object.keys(filtered).length > 0 ? filtered : null;
}

export default function TPMultiCLIReportTableGrid(props: {
    reportData: TPTaxReportMapResponse;
    services: ServiceCollection;
    fetchReportLoading: boolean;
    reportMetaData?: ReportMetaData;
    isPassthroughDrilldownEnabled?: boolean;
    // Gated behind the reportCurrencyOverlay AppConfig flag. When false, CLI headers/tab labels
    // and the per-CLI currency line render without FRAMES currency (pre-feature behaviour).
    showCliCurrency?: boolean;
    // LEF segment drill-down (lefSegmentDrillDownEnabled). Off/absent for CWB, which passes none of
    // these and therefore keeps rendering reportContent.TP_PNL with no control bar.
    lefSegmentDrillDownEnabled?: boolean;
    // Selection state is owned by the report view so it outlives Functional Segment and page changes.
    selectedDrillDownDimensionId?: string;
    onDrillDownDimensionChange?: (dimensionId: string) => void;
    showZeroValueSegments?: boolean;
    onShowZeroValueSegmentsChange?: (showZeroValueSegments: boolean) => void;
    // LEF CLI provenance (lefCliProvenanceEnabled, Stories 3.1 + 3.2). Supplied by the LEF view only
    // while drill-in is allowed (flag on and Functional Segment = "All"); CWB and MAR never pass it,
    // so their columns are untouched.
    segmentRequestContext?: SegmentRequestContext | null;
    knownSegmentCategories?: ReadonlyArray<string> | null;
}) {
    const history = useHistory();
    // Stories 3.3–3.5: permissions and the source-chip period, provided by the LEF view (none for CWB).
    const { canOpenCalcBuilder, sourceExecutionPeriod, canOpenSourceModule } = React.useContext(LefCliProvenanceContext);

    const reportHeaders = props.reportData?.reportHeaders ?? [];
    const fetchReportPage = useCallback(
        (request: FetchReportRequest) => props.services.reportsService.fetchReportDataPage(request),
        [props.services]
    );
    const provenance = useSegmentCliExpansion({
        context: props.segmentRequestContext ?? null,
        fetchPage: fetchReportPage,
        knownCategories: props.knownSegmentCategories,
    });
    const provenanceActive = provenance.enabled && hasDrillableSegments(reportHeaders);
    const lookupCliRecordId = useCallback(
        async (cli: string) =>
            (await props.services.cliService.fetchCLIDetailsForCalculationNumber(cli))?.cli?.recordId,
        [props.services]
    );
    const provenanceColumns: ProvenanceColumn[] | null = provenanceActive
        ? buildProvenanceColumns(reportHeaders, provenance.expandedClisBySegment)
        : null;

    // Story 3.3: source chips for every revealed CLI, fetched lazily and cached per report context.
    const revealedClis = provenanceColumns
        ? provenanceColumns.filter((column) => column.kind === 'cli').map((column) => column.id)
        : [];
    const cliSourceFetchers: CliSourcesFetchers = useMemo(
        () => ({
            // One POST /cli-run-sources call for up to 50 revealed CLIs. The GL chip's Calc Builder link is
            // resolved on click instead (see openCliInCalcBuilder), so the chips do not wait for it.
            fetchRunSourcesBatch: (clis: string[], period: string) =>
                props.services.dataSourcesService.fetchCliRunSources(period, clis),
        }),
        [props.services]
    );
    const cliSources = useCliSources({
        enabled: provenanceActive && !!sourceExecutionPeriod,
        contextKey: props.segmentRequestContext?.contextKey ?? null,
        executionPeriod: sourceExecutionPeriod,
        clis: revealedClis,
        fetchers: cliSourceFetchers,
    });

    // Drill-down resolution. With the flag off this pins to Account, whose reportContentKey IS
    // TP_PNL and whose filters are all no-ops, so the section renders exactly as it did before.
    const drillDownEnabled = props.lefSegmentDrillDownEnabled ?? false;
    const dimension = getDrillDownDimension(
        drillDownEnabled ? props.selectedDrillDownDimensionId : LEF_DRILLDOWN_DIMENSION_ACCOUNT_ID
    );
    const showZeroValueSegments = props.showZeroValueSegments ?? false;
    const { available: dimensionAvailable, rows: dimensionRows } = resolveDimensionView(
        props.reportData?.reportContent,
        dimension
    );
    // Revealed CLI values are folded into the rows first, so the zero filter judges a row on
    // everything visible. With nothing expanded this returns dimensionRows unchanged.
    const mergedRows = provenanceActive
        ? mergeCliValuesIntoRows(dimensionRows, dimension.reportContentKey, provenance.expandedSegmentData)
        : dimensionRows;
    const tpPnlRows = showZeroValueSegments ? mergedRows : filterZeroSegmentRows(mergedRows, dimension);

    // Story 1.3: split a segment's `"<code>  <description>"` displayName so the code reads as a code.
    // Section and account rows fall through to the payload's displayName untouched, so only the
    // dimension hierarchy changes appearance — and only in a non-Account view.
    const renderSegmentAttributeName = (item: TPTaxReportData): React.ReactNode => {
        if (!isSegmentRow(item, dimension)) {
            return item.displayName;
        }
        const { code, description } = splitSegmentDisplayName(item.displayName);
        if (!code) {
            return item.displayName;
        }
        return (
            <span>
                <Box variant="strong" display="inline">
                    {code}
                </Box>
                {`  ${description ?? SEGMENT_DESCRIPTION_UNAVAILABLE}`}
            </span>
        );
    };

    // CLI column headers/tab labels in the LEF & CWB (multi-CLI) views are annotated with the
    // FRAMES currency sourced from the DETAIL section, e.g. "<cli>" -> "<cli> (USD)".
    // Only when the currency-display feature flag is on; otherwise everything falls back to raw CLIs.
    const showCliCurrency = props.showCliCurrency ?? false;
    const cliHeaderLabels = showCliCurrency
        ? buildCliHeaderLabels(props.reportData, props.reportData?.reportHeaders)
        : undefined;
    const cliCurrencyMap = showCliCurrency ? buildCliCurrencyMap(props.reportData) : {};

    // TP P&L columns. Without provenance: the payload's headers, exactly as before. With it:
    // reconciliation columns pinned first, each segment total followed by its revealed CLIs.
    const tpPnlColumnHeaders = provenanceColumns ? provenanceColumns.map((column) => column.id) : reportHeaders;
    const captionsByCli: { [cli: string]: { caption: string; currency?: string } } = {};
    provenance.expandedSegmentData.forEach((segment) => Object.assign(captionsByCli, segment.captions));
    const tpPnlHeaderRenderers: { [column: string]: React.ReactNode } | undefined = provenanceColumns
        ? provenanceColumns.reduce((renderers, column) => {
              if (column.kind === 'segmentTotal') {
                  renderers[column.id] = (
                      <SegmentTotalHeader
                          label={cliHeaderLabels?.[column.id] ?? column.id}
                          segmentName={segmentCategoryFromHeader(column.id, props.knownSegmentCategories)}
                          status={provenance.statusOf(column.id)}
                          onToggle={() => provenance.toggleSegment(column.id)}
                      />
                  );
              } else if (column.kind === 'cli') {
                  const caption = captionsByCli[column.id];
                  const currency = showCliCurrency ? caption?.currency : undefined;
                  renderers[column.id] = (
                      <CliColumnHeader
                          label={currency ? `${column.id} (${currency})` : column.id}
                          caption={caption?.caption}
                          cli={column.id}
                          calcBuilder={{
                              canOpen: canOpenCalcBuilder,
                              onOpen: (step?: number) => openCliInCalcBuilder(column.id, lookupCliRecordId, window, step),
                          }}
                          sources={sourceExecutionPeriod ? cliSources[column.id] ?? { status: 'loading' } : undefined}
                          canOpenModule={canOpenSourceModule}
                      />
                  );
              }
              return renderers;
          }, {} as { [column: string]: React.ReactNode })
        : undefined;

    return (
        <SpaceBetween size={'xl'}>
            <ExpandableSection variant="container" headerText="Details" disableContentPaddings={true}>
                <TPTaxReportTableGrid
                    columnHeaders={props.reportData?.reportHeaders}
                    columnHeaderLabels={cliHeaderLabels}
                    data={props.reportData?.reportContent?.DETAIL}
                    services={props.services}
                    loadingStatus={props.fetchReportLoading}
                />
            </ExpandableSection>
            <ExpandableSection
                variant="container"
                headerText={IS_TESTED_PARTY_REPORT_ENABLED ? 'Tested Party TP P&L' : 'TP Profit and Loss (P&L)'}
                disableContentPaddings={true}
                headerActions={
                    props.reportMetaData?.reportName === CONSTANTS.REPORT_TYPES.TP_TAX_CWB_REPORT &&
                    !!props.reportMetaData?.workbookLabel ? (
                        <Button
                            onClick={() => {
                                history.push(
                                    `${MANAGE_EXCEPTIONS_TAB_URL_PREFIX}?workbookLabel=${encodeURIComponent(props.reportMetaData?.workbookLabel as string)}`
                                );
                            }}>
                            {' '}
                            Manage P&L Mapping{' '}
                        </Button>
                    ) : null
                }>
                {drillDownEnabled && (
                    <DrillDownDimensionSelector
                        selectedDimensionId={dimension.id}
                        onDimensionChange={(dimensionId) => props.onDrillDownDimensionChange?.(dimensionId)}
                        showZeroValueSegments={showZeroValueSegments}
                        onShowZeroValueSegmentsChange={(checked) => props.onShowZeroValueSegmentsChange?.(checked)}
                        disabled={props.fetchReportLoading}
                    />
                )}
                {/* Story 1.6a: a dimension the report has no rows for gets an explicit message. Never
                    an empty or all-zero grid, which would read as real data. */}
                {!dimensionAvailable ? (
                    <Box padding={{ horizontal: 'l', bottom: 'l' }}>
                        <Alert type="info" header={`${dimension.label} breakdown unavailable`}>
                            {dimensionUnavailableMessage(dimension)}
                        </Alert>
                    </Box>
                ) : (
                    <TPTaxReportTableGrid
                        columnHeaders={tpPnlColumnHeaders}
                        columnHeaderLabels={cliHeaderLabels}
                        columnHeaderRenderers={tpPnlHeaderRenderers}
                        stickyFirstColumns={provenanceColumns ? stickyColumnCount(provenanceColumns) : undefined}
                        data={tpPnlRows}
                        services={props.services}
                        loadingStatus={props.fetchReportLoading}
                        renderAttributeName={isAccountDimension(dimension) ? undefined : renderSegmentAttributeName}
                        stickyHeader={drillDownEnabled}
                    />
                )}
            </ExpandableSection>
            <ExpandableSection
                variant="container"
                headerText={IS_TESTED_PARTY_REPORT_ENABLED ? 'Tested Party PLI' : 'Profit Level Indicator (PLI)'}
                disableContentPaddings={true}
                defaultExpanded={true}>
                <Tabs
                    tabs={Object.entries(props.reportData?.reportContent || {})
                        .filter(([key]) => isPliTabKey(key))
                        .map(([key, value]) => ({
                            label: formatCliWithCurrency(key, cliCurrencyMap),
                            id: key,
                            content: (
                                <TPTaxReportTableGrid
                                    data={value}
                                    services={props.services}
                                    columnHeaders={props.reportData?.reportHeaders}
                                    columnHeaderLabels={cliHeaderLabels}
                                    loadingStatus={props.fetchReportLoading}
                                />
                            ),
                        }))}
                />
            </ExpandableSection>
            {props.isPassthroughDrilldownEnabled && (
                <>
                    <MultiCLIPassthroughCostSection
                        cliPassthroughDetails={filterCliMap(props.reportData?.cliPassthroughDetails, props.reportData?.filteredHeaders ?? props.reportData?.reportHeaders)}
                        serviceCollection={props.services}
                        cliCurrencyMap={cliCurrencyMap}
                    />
                    <MultiCLIPLIReconciliationSection
                        cliPliReconciliation={filterCliMap(props.reportData?.cliPliReconciliation, props.reportData?.filteredHeaders ?? props.reportData?.reportHeaders)}
                        serviceCollection={props.services}
                        cliCurrencyMap={cliCurrencyMap}
                    />
                </>
            )}
        </SpaceBetween>
    );
}
